import {
  ChangeDetectionStrategy,
  Component,
  signal,
  ViewChild,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { MatSidenav } from '@angular/material/sidenav';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { CONSTANTS_DETAILS } from './../../model';
import { MatMenuModule } from '@angular/material/menu';
import { DialogService } from '../../../services/dialog/dialog.service';
import { NewRecipeDialogComponent } from '../../../modules/recipe/new-recipe-dialog/new-recipe-dialog.component';
import { NewIngredientDialogComponent } from '../../../modules/library/new-ingredient-dialog/new-ingredient-dialog.component';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { RolesPrivilegesDirective } from '../../../shared/directives/roles-privileges/roles-privileges.directive';
import { AuthService } from '../../../services/auth';
import { UnsavedChangesService } from '../../../services/recipe-services/unsaved-changes.service';
import { SideNavMenu } from '../../model/side-nav-interface';
@Component({
  selector: 'app-side-nav',
  imports: [
    TranslateModule,
    RouterOutlet,
    MatSidenavModule,
    MatListModule,
    RouterLink,
    RouterLinkActive,
    MatMenuModule,
    MatIconModule,
    MatSidenavModule,
    CommonModule,
    RolesPrivilegesDirective,
    NgOptimizedImage,
  ],
  templateUrl: './side-nav.component.html',
  styleUrl: './side-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavComponent {
  @ViewChild('sidenav') sidenav!: MatSidenav;
  public isSidebarCollapsed: boolean = true;
  public APP_MENUS = CONSTANTS_DETAILS.APP_MENUS;
  public activeDialogMenuIndex = signal<number | null>(null);
  public recipeDetailsSource = signal<string | null>(null);
  public removePadding = signal<boolean>(false);

  expandedMenus: boolean[] = [];

  constructor(
    private _dialogService: DialogService,
    private _router: Router,
    public _auth: AuthService,
    private _route: ActivatedRoute,
    private _unsavedChangesService: UnsavedChangesService,
  ) {
    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.recipeDetailsSource.set(
          typeof window.history.state?.source === 'string'
            ? (window.history.state.source as string)
            : null,
        );
      }
      this.removePadding.set(this._router.url.includes('kpi-dashboard'));
    });
  }

  /**
   * Toggles the sidebar state between collapsed and expanded.
   * This method is called when the user clicks on the toggle button.
   *
   * @memberof SideNavComponent
   */
  public async toggleSidebar() {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    if (this.isSidebarCollapsed) {
      this.expandedMenus = [];
    }
  }

  /**
   * toggles the expansion state of a specific menu.
   * @param index
   */
  toggleMenuExpansion(index: number) {
    this.expandedMenus[index] = !this.expandedMenus[index];
  }

  /**
   * Opens the dialog for creating a new recipe.
   * This method is called when the user clicks on the New Recipe button.
   *
   * @memberof SideNavComponent
   * Use `onCreateNewRecipe` instead.
   */
  public createNewRecipe() {
    this._dialogService.openDialog(NewRecipeDialogComponent, {
      data: {},
    });
  }

  public async handleMenuAction(action: string, index: number) {
    let dialogRef;
    if (action === 'createNewRecipe') {
      const detailsComponent = this._unsavedChangesService.detailsComponent;
      if (detailsComponent) {
        const canLeave = await detailsComponent.confirmUnsavedChanges();
        if (!canLeave) return;
      }
      this.activeDialogMenuIndex.set(index);
      dialogRef = this._dialogService.openDialog(NewRecipeDialogComponent, {
        data: {},
      });
    }
    if (dialogRef) {
      dialogRef.afterClosed().subscribe(() => {
        this.activeDialogMenuIndex.set(null);
      });
    }
  }

  /**
   * Opens the dialog for creating a new recipe.
   * This method is called when the user clicks on the New Recipe button.
   *
   * @memberof SideNavComponent
   */
  public handleMenuClick(menuItem: SideNavMenu): void {
    if (
      menuItem.label ===
      CONSTANTS_DETAILS.APP_MENU_LABELS.NEW_INGREDIENT_MENU_LABEL
    ) {
      this._dialogService.openDialog(NewIngredientDialogComponent, {
        data: {},
      });
    }
  }

  public isChildRouteActive(menu: unknown): boolean {
    const typedMenu = menu as SideNavMenu;
    if (!typedMenu.children) return false;
    return typedMenu.children.some(
      (child) => !!child.link && this._router.url.startsWith(child.link),
    );
  }
}
